import React from 'react';
import {withRouter} from 'react-router';
import PropTypes from 'prop-types';
import {fromJS} from 'immutable';
import moment from 'moment';
import {pluralize} from 'lib/stringUtils';
import {stats} from 'lib/StatsUtil';
import {renderDuration} from 'lib/timeUtils';
import {resource} from '@albert-io/json-api-framework/request/builder';
import {setUpStore} from 'client/framework';
import SettingsTableStore from 'sg/Tables/SettingsTable/SettingsTable.store';
import SettingsTable from 'sg/Tables/SettingsTable/SettingsTable.react';
import {Column} from 'sg/Tables/MandarkTable/MandarkTable.react';
import FlatBarChart from 'generic/FlatBarChart/FlatBarChart.react';
import PerformanceIndicator from 'generic/PerformanceIndicator/PerformanceIndicator.react';
import EmptyInsightsReport from '../../EmptyInsightsReport/EmptyInsightsReport.react';
import classroomInsightsStore from '../../../ClassroomInsights.store';

const settingsTableStore = setUpStore(SettingsTableStore, 'StandardInsightsStudentsTableStore');

class StudentsTable extends React.Component {
  static propTypes = {
    location: PropTypes.shape({
      query: PropTypes.shape({
        attempt: PropTypes.string,
        endDate: PropTypes.string,
        guideLevelId: PropTypes.string,
        startDate: PropTypes.string,
        subjectId: PropTypes.string
      })
    }),
    params: PropTypes.shape({
      classId: PropTypes.string,
      standardId: PropTypes.string
    })
  };

  getStudentsQuery() {
    const {attempt, endDate, guideLevelId, startDate, subjectId} = this.props.location.query;
    const guessContext = {};
    if (attempt === 'mostRecent') {
      guessContext.most_recent = true;
    } else {
      guessContext.attempt = attempt;
    }

    return resource('students_v2')
      .mandarkEndpoint(['classrooms_v1', this.props.params.classId, 'students_v2'])
      .withMeta('student_v2')
      .meta({
        context: {
          end_date: endDate,
          guess: guessContext,
          label: {
            id: this.props.params.standardId
          },
          start_date: startDate,
          subject: {
            ids: subjectId
          }
        }
      })
      .meta(
        {
          context: {
            guideLevelId: {
              id: guideLevelId
            }
          }
        },
        guideLevelId
      );
  }

  renderAnsweredCorrectly(student) {
    const studentMeta = student.getMeta();
    const correctGuesses = studentMeta.getCountOfCorrectGuesses();
    const totalGuesses = studentMeta.getCountOfGuesses();
    const unansweredQuestions = studentMeta.getCountOfUnansweredQuestions();
    const totalPotentialGuesses = totalGuesses + unansweredQuestions;

    const performanceData = fromJS([
      {
        fillColorClass: 'classroom-insights__performance--correct',
        fillPercentage: stats.percentage(correctGuesses, totalPotentialGuesses),
        keyLabel: 'Correct'
      },
      {
        fillColorClass: 'classroom-insights__performance--incorrect',
        fillPercentage: stats.percentage(totalGuesses - correctGuesses, totalPotentialGuesses),
        keyLabel: 'Incorrect'
      },
      {
        fillColorClass: 'classroom-insights__performance--unanswered',
        fillPercentage: stats.percentage(unansweredQuestions, totalPotentialGuesses),
        keyLabel: 'Not Answered'
      }
    ]);

    return (
      <div className='usage-report__performance'>
        <small>
          {totalGuesses} {pluralize('total attempt', totalGuesses)}
        </small>
        <FlatBarChart inline data={performanceData} />
      </div>
    );
  }

  renderCorrectAttempts(student) {
    return student.getMeta().getCountOfCorrectGuesses();
  }

  renderIncorrectAttempts(student) {
    const studentMeta = student.getMeta();
    return studentMeta.getCountOfGuesses() - studentMeta.getCountOfCorrectGuesses();
  }

  renderMastery(student) {
    const studentMeta = student.getMeta();
    const correctGuesses = studentMeta.getCountOfCorrectGuesses();
    const totalGuesses = studentMeta.getCountOfGuesses();

    return (
      <PerformanceIndicator
        numberCorrect={correctGuesses}
        total={totalGuesses}
        isUnavailable={totalGuesses === 0}
        small
      />
    );
  }

  renderName(student) {
    return student.getFullName({formal: true});
  }

  renderTimeSpent(student) {
    const timeSpent = moment.duration(student.getMeta().getSumOfGuessesTimeElapsed());
    const formattedTimeSpent = renderDuration(timeSpent, {style: 'hh:mm:ss'});
    return formattedTimeSpent;
  }

  renderTotalAttempts(student) {
    return student.getMeta().getCountOfGuesses();
  }

  render() {
    const emptyResultsHeader = settingsTableStore.getSearchTerm()
      ? `No students matching your search term for this standard.`
      : 'No students for this standard yet.';

    const emptyResultsContent = settingsTableStore.getSearchTerm()
      ? 'Once students attempt a question, for this standard, those students will appear here.'
      : 'Once students attempt a question, for this standard, those student will appear here.';

    return (
      <SettingsTable
        emptyTableElement={
          <EmptyInsightsReport header={emptyResultsHeader} content={emptyResultsContent} />
        }
        query={this.getStudentsQuery()}
        searchFilters={classroomInsightsStore.getStudentsSearchFilters}
        storeName={settingsTableStore.getName()}
      >
        <Column
          defaultSortParam
          defaultVisible
          content={this.renderName}
          heading='Name'
          sortBy='last_name'
        />
        <Column
          align='right'
          content={this.renderTimeSpent}
          defaultVisible
          heading='Average Time Spent'
          sortBy='meta.sum_of_guesses_time_elapsed'
        />
        <Column
          align='center'
          content={this.renderAnsweredCorrectly}
          defaultVisible
          heading='Progress'
        />
        <Column align='center' content={this.renderMastery} defaultVisible heading='Mastery' />
        <Column
          align='right'
          content={this.renderCorrectAttempts}
          heading='Correct Attempts'
          sortBy='meta.count_of_correct_guesses'
        />
        <Column align='right' content={this.renderIncorrectAttempts} heading='Incorrect Attempts' />
        <Column
          align='right'
          content={this.renderTotalAttempts}
          heading='Total Attempts'
          sortBy='meta.count_of_guesses'
        />
      </SettingsTable>
    );
  }
}

export default withRouter(StudentsTable);
